import {
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';
import {object} from 'yup';

import {
  useGetAutostacjaSvConnectorConfigQuery,
  usePatchAutostacjaSvConnectorConfigMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, yupString} from 'shared/web';

interface AutostacjaSettingsSubscriptionKeyFormType {
  subscriptionKey: string;
}

const schema = object({
  subscriptionKey: yupString.required(),
});

export function AutostacjaSettingsSubscriptionKeyForm() {
  const {
    data: autostacjaQueryData,
    isLoading: isAutostacjaQueryLoading,
    isError: isAutostacjaQueryError,
  } = useGetAutostacjaSvConnectorConfigQuery();
  const [patchAutostacjaSvConnectorConfigMutation, {isLoading: isPatchAutostacjaMutationLoading}] =
    usePatchAutostacjaSvConnectorConfigMutation();

  const defaultValues = {
    subscriptionKey: autostacjaQueryData?.credentials?.subscriptionKey ?? '',
  };

  const onSubmit: FormSubmitHandler<AutostacjaSettingsSubscriptionKeyFormType> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await patchAutostacjaSvConnectorConfigMutation({
      body: {
        credentials: {
          subscriptionKey: values.subscriptionKey,
        },
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <DataStatus isLoading={isAutostacjaQueryLoading} isError={isAutostacjaQueryError}>
      <Form<AutostacjaSettingsSubscriptionKeyFormType>
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        schema={schema}
        shouldWatchForUnsavedChanges
      >
        {(control) => (
          <Grid columns={12}>
            <GridItem span={12}>
              <FormField
                control={control}
                name="subscriptionKey"
                label={i18n.t('page.integrations.labels.autostacjaSubscriptionKey')}
                type="text"
                data-testid={testIds.settings.autostacjaSettingsSubscriptionKey('subscriptionKey')}
                isDisabled={isPatchAutostacjaMutationLoading}
              />
            </GridItem>
            <GridItem span={12}>
              <ButtonGroup align="right">
                <FormButton
                  title={i18n.t('general.actions.save')}
                  control={control}
                  type="submit"
                  data-testid={testIds.settings.autostacjaSettingsSubscriptionKey('submit')}
                  isLoading={isPatchAutostacjaMutationLoading}
                />
              </ButtonGroup>
            </GridItem>
          </Grid>
        )}
      </Form>
    </DataStatus>
  );
}
